import { Controller } from "@hotwired/stimulus"

// The doors' address notice modal (shared/_address_notice_modal.html.erb).
// Bounced: Verify again calls the one re-verify door; a clear result turns
// the button into the retry (submit the form again, or post the retry URL);
// Allow it anyway (senior staff) asks for a reason first. Unsubscribed: one
// button, Got it. Escape and the backdrop close it.
export default class extends Controller {
  static targets = ["message", "reasonBlock", "reason", "error", "verifyBtn", "allowBtn"]
  static values = { email: String, retry: String, retryUrl: String, reverifyUrl: String, canAllow: Boolean }

  connect() {
    document.body.classList.add("overflow-hidden")
    this.keydown = (e) => { if (e.key === "Escape") this.close() }
    document.addEventListener("keydown", this.keydown)
    this.element.addEventListener("click", (e) => { if (e.target === this.element) this.close() })
    this.element.querySelector(".app-modal-btn-confirm")?.focus()
  }

  disconnect() {
    document.body.classList.remove("overflow-hidden")
    document.removeEventListener("keydown", this.keydown)
  }

  close(event) {
    event?.preventDefault()
    this.element.remove()
    document.body.classList.remove("overflow-hidden")
  }

  async verify(event) {
    event?.preventDefault()
    if (this.cleared) return this.retry()
    this.busy(true, "Checking...")
    const res = await this.send({ email: this.emailValue })
    this.busy(false, "Verify again with Hunter.io")
    this.setMessage(res.message || "Could not check right now.")
    if (res.outcome === "cleared") this.markCleared()
    else if (res.outcome === "catch_all" || res.outcome === "could_not_check") {
      if (this.hasAllowBtnTarget) this.allowBtnTarget.textContent = "Allow it anyway"
    }
  }

  async allow(event) {
    event?.preventDefault()
    if (!this.hasReasonBlockTarget) return
    if (this.reasonBlockTarget.hidden) {
      this.reasonBlockTarget.hidden = false
      this.reasonTarget.focus()
      this.allowBtnTarget.textContent = "Allow it with this reason"
      return
    }
    const reason = this.reasonTarget.value.trim()
    if (!reason) { this.showError("Say why in a few words; it is recorded under your name."); this.reasonTarget.focus(); return }
    this.busy(true, "Allowing...")
    const res = await this.send({ email: this.emailValue, reason })
    this.busy(false, "Verify again with Hunter.io")
    if (res.outcome !== "cleared") { this.showError(res.message || "Could not allow this address."); return }
    this.setMessage("Allowed and recorded under your name.")
    this.markCleared()
  }

  markCleared() {
    this.cleared = true
    if (this.hasReasonBlockTarget) this.reasonBlockTarget.hidden = true
    if (this.hasAllowBtnTarget) this.allowBtnTarget.hidden = true
    this.verifyBtnTarget.textContent = this.retryValue === "form" ? "Save contact" : (this.retryUrlValue ? "Continue" : "Done")
  }

  retry() {
    if (this.retryValue === "form") {
      const form = document.querySelector("form[action*='/pr/contacts']")
      if (form) { this.close(); form.requestSubmit(); return }
    }
    if (this.retryUrlValue) {
      const form = document.createElement("form")
      form.method = "post"
      form.action = this.retryUrlValue
      form.dataset.turbo = "false"
      const token = document.createElement("input")
      token.type = "hidden"; token.name = "authenticity_token"; token.value = this.csrf()
      form.appendChild(token)
      document.body.appendChild(form)
      form.submit()
      return
    }
    window.location.reload()
  }

  async send(body) {
    try {
      const res = await fetch(this.reverifyUrlValue, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.csrf() },
        body: JSON.stringify(body)
      })
      return await res.json().catch(() => ({ outcome: "could_not_check", message: "Couldn't reach TEAM just now." }))
    } catch (e) {
      return { outcome: "could_not_check", message: "The connection dropped. Try again." }
    }
  }

  csrf() { return document.querySelector("meta[name='csrf-token']")?.content || "" }
  setMessage(text) { if (this.hasMessageTarget) this.messageTarget.textContent = text }
  showError(text) { if (!this.hasErrorTarget) return; this.errorTarget.textContent = text; this.errorTarget.hidden = !text }
  busy(on, label) { if (this.hasVerifyBtnTarget) { this.verifyBtnTarget.disabled = on; this.verifyBtnTarget.textContent = label }; if (this.hasAllowBtnTarget) this.allowBtnTarget.disabled = on }
}
